import React from 'react';

import { css } from '@emotion/css';
import { colors } from 'theme/colors';

import DeadStockIcon from 'assets/svgs/cwd-card-dead-stock.svg';
import DemandShiftIcon from 'assets/svgs/cwd-card-demand-shift.svg';
import OverstockIcon from 'assets/svgs/cwd-card-overstock.svg';
import StockoutRiskIcon from 'assets/svgs/cwd-card-stockout-risk.svg';

type StarterTagColor = 'warning' | 'neutral' | 'danger' | 'success';

const TAG_COLORS: Record<StarterTagColor, { bg: string; fg: string }> = {
  warning: { bg: colors.warning[200], fg: colors.warning[700] },
  neutral: { bg: colors.neutral[200], fg: colors.neutral[600] },
  danger: { bg: colors.danger[200], fg: colors.danger[600] },
  success: { bg: colors.success[200], fg: colors.success[600] },
};

const STARTER_CARDS: Array<{
  tag: string;
  tagColor: StarterTagColor;
  icon: React.ReactNode;
  question: string;
  subtitle: string;
}> = [
  {
    tag: 'Overstock',
    tagColor: 'warning',
    icon: <OverstockIcon width={15} height={15} />,
    question: 'Where am I most overstocked right now?',
    subtitle: 'Excess by branch and product group',
  },
  {
    tag: 'Dead stock',
    tagColor: 'neutral',
    icon: <DeadStockIcon width={15} height={15} />,
    question: 'Which dead stock is tying up the most cash?',
    subtitle: 'No sales in 12 months, still on hand',
  },
  {
    tag: 'Stockout risk',
    tagColor: 'danger',
    icon: <StockoutRiskIcon width={15} height={15} />,
    question: 'Which A items am I about to stock out of?',
    subtitle: 'Coverage below lead time on top movers',
  },
  {
    tag: 'Demand shift',
    tagColor: 'success',
    icon: <DemandShiftIcon width={15} height={15} />,
    question: 'Which slow movers are picking up speed?',
    subtitle: 'Quiet items with demand now climbing',
  },
];

const ADDITIONAL_STARTERS = ['Late suppliers', 'Fill rate by branch', 'Open POs past due'];

/** Empty-state prompt cards shown before a conversation begins on the full-page Chat with Data view. */
export function ChatStarters({ onSend }: { onSend: (text: string) => void }) {
  return (
    <div
      className={css`
        max-width: 720px;
        margin: 0 auto;
        width: 100%;
      `}
    >
      <div
        className={css`
          font-size: 24px;
          font-weight: 700;
          color: ${colors.neutral[900]};
          line-height: 1.3;
        `}
      >
        What do you want to learn about your inventory today?
      </div>
      <div
        className={css`
          font-size: 15px;
          color: ${colors.neutral[500]};
          margin: 4px 0 24px;
        `}
      >
        Pick a starting point or ask your own question.
      </div>

      <div
        className={css`
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        `}
      >
        {STARTER_CARDS.map((card) => (
          <button
            type="button"
            key={card.tag}
            className={css`
              display: flex;
              flex-direction: column;
              gap: 8px;
              text-align: left;
              padding: 16px;
              background: ${colors.neutral[0]};
              border: 1px solid ${colors.neutral[300]};
              border-radius: 10px;
              cursor: pointer;
              transition: border-color 0.15s ease, box-shadow 0.15s ease;

              &:hover {
                background: ${colors.primary[100]};
                border-color: ${colors.primary[400]};
                box-shadow: 0 2px 8px rgba(24, 24, 40, 0.06);
              }
            `}
            onClick={() => onSend(card.question)}
          >
            <span
              className={css`
                display: inline-flex;
                align-items: center;
                gap: 6px;
                align-self: flex-start;
                padding: 3px 10px;
                border-radius: 6px;
                font-size: 13px;
                font-weight: 600;
                color: ${TAG_COLORS[card.tagColor].fg};
                background: ${TAG_COLORS[card.tagColor].bg};
              `}
            >
              {card.icon}
              {card.tag}
            </span>
            <div
              className={css`
                font-size: 16px;
                font-weight: 600;
                color: ${colors.neutral[800]};
                line-height: 1.35;
              `}
            >
              {card.question}
            </div>
            <div
              className={css`
                font-size: 13px;
                color: ${colors.neutral[500]};
              `}
            >
              {card.subtitle}
            </div>
          </button>
        ))}
      </div>

      <div
        className={css`
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: center;
          gap: 8px;
          margin-top: 20px;
        `}
      >
        <span
          className={css`
            color: ${colors.neutral[400]};
          `}
        >
          Also try
        </span>
        {ADDITIONAL_STARTERS.map((prompt) => (
          <button
            type="button"
            key={prompt}
            className={css`
              padding: 6px 14px;
              font-size: 14px;
              color: ${colors.neutral[700]};
              background: ${colors.neutral[0]};
              border: 1px solid ${colors.neutral[300]};
              border-radius: 8px;
              cursor: pointer;
              transition: border-color 0.15s ease;

              &:hover {
                border-color: ${colors.primary[400]};
              }
            `}
            onClick={() => onSend(prompt)}
          >
            {prompt}
          </button>
        ))}
      </div>
    </div>
  );
}
